import { getCapsuleDisplayKind, isSelfOnlyCapsule } from "../../utils/capsuleDisplay";
import IconTimeCapsuleLockedMarker from "../../assets/icons/v2/time-capsule-locked.webp";
import IconTimeCapsuleUnlockedMarker from "../../assets/icons/v2/time-capsule-opened.webp";
import IconTimeCapsuleUnOpened from "../../assets/icons/v2/time-capsule-closed.webp";
import IconTreasureClosedMarker from "../../assets/icons/v2/treasure-box-locked.webp";
import IconTreasureOpenedMarker from "../../assets/icons/v2/treasure-box-unlocked.webp";
import IconTreasureFeaturedMarker from "../../assets/icons/v2/treasure-box-featured.webp";
import IconMarkerPublic from "../../assets/icons/v2/map-pin-public.webp";
import IconMarkerPublicWebp from "../../assets/icons/v2/map-pin-public.webp";
import IconSeededPublicCapsule from "../../assets/icons/v2/map-pin-seeded.webp";
import IconDraftCapsule from "../../assets/icons/v2/time-capsule-draft.webp";
import IconSentCapsule from "../../assets/icons/v2/time-capsule-sent.webp";
import IconTimeCapsuleMemory from "../../assets/icons/v2/time-capsule-memory.webp";
import IconInsiderStar from "../../assets/icons/v2/insider-star.webp";
import {
  hasInsiderAccess,
  shouldGateMemberExclusiveContent,
} from "../../utils/insiderAccess";
import { resolveCapsuleThumbnailMedia } from "../../utils/timeCapsuleThumbnail";
import {
  buildBusinessCapsuleLabels,
  resolveBusinessLocationForCapsule,
  resolveBusinessLocationLabel,
} from "../../utils/businessLocationLabel";

export const HOME_MAP_DEFAULT_FETCH_RADIUS_METERS = 15000;
export const HOME_MAP_MAX_FETCH_RADIUS_METERS = 5000000;
export const HOME_MAP_CLUSTER_BREAK_ZOOM = 17;
export const HOME_MAP_ALWAYS_VISIBLE_CLUSTER_ZOOM = 16;
const EARTH_RADIUS_METERS = 6371000;
const ALWAYS_VISIBLE_CAPSULE_TYPES = new Set(["sponsored", "memberexclusive"]);

export function isLatLng(position) {
  return (
    position &&
    Number.isFinite(Number(position.lat)) &&
    Number.isFinite(Number(position.lng))
  );
}

export function normalizeCapsuleType(value) {
  return String(value || "").trim().toLowerCase();
}

export function isInsiderSubscriptionTier(subscriptionTier = "") {
  return hasInsiderAccess(null, subscriptionTier);
}

/**
 * Only send a viewer to the Insider upsell when we positively know they are not
 * an Insider. `subscriptionResolved` is false while the profile is still loading
 * or has degraded to an auth-only fallback — in that window a real Insider is
 * indistinguishable from a free user, and bouncing them to /membership is the
 * bug. Deferring costs a free user one extra hop: the capsule page resolves the
 * profile from Firestore and gates there.
 */
export function shouldRedirectMemberExclusiveMarkerToMembership(
  marker,
  profileOrSubscription = "",
  { subscriptionResolved = false } = {}
) {
  if (!subscriptionResolved) return false;
  return shouldGateMemberExclusiveContent(marker, profileOrSubscription);
}

export function buildLocationClusterKey(position) {
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `${lat.toFixed(6)}:${lng.toFixed(6)}`;
}

function normalizeLongitudeDelta(delta) {
  let normalized = Number(delta || 0);
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
}

export function distanceMetersBetween(a, b) {
  const lat1 = Number(a?.lat);
  const lng1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lng2 = Number(b?.lng);
  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lng1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lng2)
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(normalizeLongitudeDelta(lng2 - lng1));
  const x1 = toRad(lat1);
  const x2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(x1) * Math.cos(x2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_METERS * c;
}

function normalizeSponsoredBusinessMarkerInfo(value) {
  if (!value || typeof value !== "object") {
    return {
      avatarUrl: String(value || "").trim(),
      companyName: "",
      locations: [],
    };
  }
  return {
    avatarUrl: String(value?.avatarUrl || value?.pinUrl || value?.url || "").trim(),
    companyName: String(value?.companyName || "").trim(),
    locations: Array.isArray(value?.locations) ? value.locations : [],
  };
}

export function isSponsoredCapsuleAtBusinessLocation(capsule = {}, businessLocations = []) {
  const locations = Array.isArray(businessLocations) ? businessLocations : [];
  // A business with no registered locations can't contradict the capsule, so it
  // keeps its pin.
  if (!locations.length) return true;
  return resolveBusinessLocationForCapsule(capsule, locations) !== null;
}

function toViewportCenter(viewport = {}) {
  const candidate = viewport?.center;
  return isLatLng(candidate)
    ? { lat: Number(candidate.lat), lng: Number(candidate.lng) }
    : null;
}

export function resolveHomeMapReturnCenter(targetPosition = null, viewport = {}, fallbackCenter = null) {
  if (isLatLng(targetPosition)) {
    return {
      lat: Number(targetPosition.lat),
      lng: Number(targetPosition.lng),
    };
  }
  const viewportCenter = toViewportCenter(viewport);
  if (viewportCenter) return viewportCenter;
  if (isLatLng(fallbackCenter)) {
    return {
      lat: Number(fallbackCenter.lat),
      lng: Number(fallbackCenter.lng),
    };
  }
  return null;
}

export function buildHomeMapNavigationState({
  targetPath = "",
  viewport = {},
  fallbackCenter = null,
  extraState = {},
} = {}) {
  const returnCenter = resolveHomeMapReturnCenter(null, viewport, fallbackCenter);
  const path = String(targetPath || "").trim();
  return {
    path,
    homeState: returnCenter ? { center: returnCenter } : {},
    targetState: {
      ...(extraState && typeof extraState === "object" ? extraState : {}),
      source: "home-map",
      ...(returnCenter ? { returnCenter } : {}),
    },
  };
}

export function buildHomeMapFetchRadiusMeters(
  viewport = {},
  fallbackRadius = HOME_MAP_DEFAULT_FETCH_RADIUS_METERS,
  maxRadius = HOME_MAP_MAX_FETCH_RADIUS_METERS
) {
  const center = toViewportCenter(viewport);
  const bounds = viewport?.bounds || null;
  if (!center || !bounds) {
    return Math.max(1000, Math.min(Number(fallbackRadius) || HOME_MAP_DEFAULT_FETCH_RADIUS_METERS, maxRadius));
  }

  const north = Number(bounds.north);
  const south = Number(bounds.south);
  const east = Number(bounds.east);
  const west = Number(bounds.west);
  if (
    !Number.isFinite(north) ||
    !Number.isFinite(south) ||
    !Number.isFinite(east) ||
    !Number.isFinite(west)
  ) {
    return Math.max(1000, Math.min(Number(fallbackRadius) || HOME_MAP_DEFAULT_FETCH_RADIUS_METERS, maxRadius));
  }

  const corners = [
    { lat: north, lng: east },
    { lat: north, lng: west },
    { lat: south, lng: east },
    { lat: south, lng: west },
  ];
  const farthestCornerDistance = corners.reduce((maxDistance, corner) => {
    const distance = distanceMetersBetween(center, corner);
    return Number.isFinite(distance) && distance > maxDistance ? distance : maxDistance;
  }, 0);

  const safeRadius = Math.max(
    Number(fallbackRadius) || HOME_MAP_DEFAULT_FETCH_RADIUS_METERS,
    farthestCornerDistance || 0
  );
  return Math.max(1000, Math.min(safeRadius, maxRadius));
}

export function buildHomeMapViewportCacheKey(viewport = {}, radiusMeters = HOME_MAP_DEFAULT_FETCH_RADIUS_METERS) {
  const center = toViewportCenter(viewport);
  const zoom = Number(viewport?.zoom);
  if (!center) return "";
  const safeRadiusMeters = Math.max(
    1000,
    Number(radiusMeters) || HOME_MAP_DEFAULT_FETCH_RADIUS_METERS
  );
  const zoomBucket = Number.isFinite(zoom) ? Math.round(zoom) : "na";
  const centerBucketMeters = Math.max(
    250,
    Math.min(Math.round(safeRadiusMeters / 4), 50000)
  );
  const radiusBucketSizeMeters = Math.max(
    250,
    Math.min(Math.round(safeRadiusMeters / 8), 100000)
  );
  const latStep = centerBucketMeters / 111320;
  const lngStep =
    centerBucketMeters /
    Math.max(111320 * Math.cos((center.lat * Math.PI) / 180), 0.000001);
  const bucketedCenterLat = Math.round(center.lat / latStep) * latStep;
  const bucketedCenterLng = Math.round(center.lng / lngStep) * lngStep;
  const bucketedRadiusMeters =
    Math.round(safeRadiusMeters / radiusBucketSizeMeters) * radiusBucketSizeMeters;
  return [
    `z:${zoomBucket}`,
    `clat:${bucketedCenterLat.toFixed(4)}`,
    `clng:${bucketedCenterLng.toFixed(4)}`,
    `r:${Math.round(bucketedRadiusMeters)}`,
  ].join("|");
}

function toSignalNumber(value) {
  // Number(null) is 0, and 0 is a meaningful price level (free) — coercing would
  // make "unknown" and "free" indistinguishable on the marker. Reject the empty
  // values before converting.
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// Rows need rating/price to be worth choosing between. placeFacts is written by
// the discover seeders but was never carried onto the marker.
function buildPlaceSignals(placeFacts) {
  const facts = placeFacts && typeof placeFacts === "object" ? placeFacts : {};
  return {
    rating: toSignalNumber(facts.rating),
    reviewCount: toSignalNumber(facts.reviewCount),
    priceLevel: toSignalNumber(facts.priceLevel),
  };
}

function buildMarkerBase({
  capsule,
  position,
  icon,
  businessAvatarUrlOverride = "",
  businessLocationLabel = "",
  companyName = "",
  senderLabel = "",
  useBusinessLabels = false,
  width = 70,
  height = 70,
  isDraft = false,
  isSent = false,
  clusterPolicy = "overlap",
}) {
  const capsuleId = String(capsule?.id || capsule?.capsuleId || capsule?.docId || "").trim();
  const rawPlaceName = String(
    capsule?.location?.placeName || capsule?.placeName || capsule?.name || ""
  ).trim();
  const resolvedLabel = String(businessLocationLabel || "").trim();
  const placeName = useBusinessLabels && resolvedLabel ? resolvedLabel : rawPlaceName;
  const businessLabels = useBusinessLabels
    ? buildBusinessCapsuleLabels({ capsule, locationLabel: resolvedLabel, companyName })
    : null;
  const address = String(
    capsule?.location?.address ||
      capsule?.address ||
      capsule?.formattedAddress ||
      capsule?.formatted_address ||
      ""
  ).trim();
  const normalizedCapsuleType = isDraft || isSent
    ? "private"
    : normalizeCapsuleType(capsule?.capsuleType || "public");
  const thumbnailMedia = resolveCapsuleThumbnailMedia(capsule);
  return {
    id: capsuleId,
    position,
    sourcePosition: position,
    locationClusterKey: buildLocationClusterKey(position),
    placeId: String(capsule?.location?.placeId || capsule?.placeId || "").trim(),
    placeName,
    title:
      businessLabels?.title ||
      capsule?.messageTitle ||
      capsule?.subject ||
      capsule?.title ||
      placeName ||
      "",
    body: capsule?.messageBody || capsule?.message || capsule?.body || "",
    heroUrl: thumbnailMedia.url || null,
    heroType: thumbnailMedia.type || null,
    address,
    icon,
    revealDate: capsule?.revealDate || null,
    capsuleType: normalizedCapsuleType,
    senderId: capsule?.senderId || "",
    senderProfileImage: capsule?.senderProfileImage || "",
    businessId: String(capsule?.businessId || capsule?.senderId || "").trim(),
    companyName: String(companyName || "").trim(),
    senderLabel: String(senderLabel || "").trim(),
    businessAvatarUrl: String(businessAvatarUrlOverride || "").trim(),
    hashtags: capsule?.hashtags,
    callToActionText: capsule?.callToActionText || null,
    callToActionUrl: capsule?.callToActionUrl || null,
    recipientUserIds: Array.isArray(capsule?.recipientUserIds) ? capsule.recipientUserIds : [],
    claimedBy: Array.isArray(capsule?.claimedBy) ? capsule.claimedBy : [],
    recipientPhoneNumbers: Array.isArray(capsule?.recipientPhoneNumbers) ? capsule.recipientPhoneNumbers : [],
    audience: capsule?.audience || "",
    isSelfCapsule: capsule?.isSelfCapsule === true,
    deliveryMode: capsule?.deliveryMode || "",
    insider: capsule?.insider === true,
    seeded: capsule?.seeded === true,
    seedType: String(capsule?.seedType || "").trim().toLowerCase(),
    placeSignals: buildPlaceSignals(capsule?.placeFacts),
    width,
    height,
    isDraft,
    isSent,
    alwaysVisible:
      capsule?.seeded === true || ALWAYS_VISIBLE_CAPSULE_TYPES.has(normalizedCapsuleType),
    clusterPolicy,
  };
}

// usersList is loaded by HomePage for exactly this purpose but was never read
// here. Same precedence the gem rows use: display name, then handle, then
// username.
function resolveMarkerSenderLabel(capsule = {}, usersList = []) {
  const senderId = String(capsule?.senderId || "").trim();
  if (!senderId) return "";
  const match = (Array.isArray(usersList) ? usersList : []).find(
    (user) => String(user?.uid || "").trim() === senderId
  );
  if (!match) return "";
  const handle = String(match.publicHandle || "").trim().replace(/^@+/, "");
  return (
    String(match.displayName || "").trim() ||
    (handle ? `@${handle}` : "") ||
    String(match.userName || "").trim()
  );
}

export function buildHomeMapMarkerFromCapsule(
  capsule,
  {
    userId = "",
    isSubscribedUser = false,
    sponsoredBusinessAvatarMap = {},
    usersList = [],
    includeDiscoverOnly = false,
  } = {}
) {
  if (capsule?.discoverOnly === true && includeDiscoverOnly !== true) return null;

  const normalizedCapsuleType = normalizeCapsuleType(capsule?.capsuleType);
  const lat = Number(
    capsule?.location?.lat ??
      capsule?.location?.latitude ??
      capsule?.lat ??
      capsule?.latitude
  );
  const lng = Number(
    capsule?.location?.lng ??
      capsule?.location?.lon ??
      capsule?.location?.longitude ??
      capsule?.lng ??
      capsule?.lon ??
      capsule?.longitude
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const position = { lat, lng };
  const revealTs = capsule?.revealDate?.toDate?.().getTime?.() ?? -Infinity;
  const isRead = Array.isArray(capsule?.readBy) ? capsule.readBy.includes(userId) : false;
  const isSelfMemory = isSelfOnlyCapsule(capsule, userId);
  const resolvedBusinessId = String(capsule?.businessId || capsule?.senderId || "").trim();

  const isBusinessCapsule =
    normalizedCapsuleType === "sponsored" || normalizedCapsuleType === "memberexclusive";
  const sponsoredMarkerInfo = isBusinessCapsule
    ? normalizeSponsoredBusinessMarkerInfo(sponsoredBusinessAvatarMap[resolvedBusinessId])
    : null;
  const matchedBusinessLocation = sponsoredMarkerInfo
    ? resolveBusinessLocationForCapsule(capsule, sponsoredMarkerInfo.locations)
    : null;
  const businessLocationLabel = matchedBusinessLocation
    ? resolveBusinessLocationLabel(matchedBusinessLocation, {
        companyName: sponsoredMarkerInfo.companyName,
      })
    : "";

  let iconSrc = IconMarkerPublic;
  if (capsule?.insider === true) {
    iconSrc = IconInsiderStar;
  } else if (capsule?.seeded === true) {
    iconSrc = IconSeededPublicCapsule;
  } else if (normalizedCapsuleType === "private") {
    iconSrc = isSelfMemory
      ? IconTimeCapsuleMemory
      : revealTs > Date.now()
      ? IconTimeCapsuleLockedMarker
      : isRead
      ? IconTimeCapsuleUnlockedMarker
      : IconTimeCapsuleUnOpened;
  } else if (normalizedCapsuleType === "memberexclusive") {
    // Always a chest — the locked/unlocked tease is the point of the tier, and
    // it never becomes a business pin however exactly it sits on a location.
    iconSrc = isSubscribedUser ? IconTreasureOpenedMarker : IconTreasureClosedMarker;
  } else if (normalizedCapsuleType === "sponsored") {
    const isOffRegisteredBusinessLocation =
      sponsoredMarkerInfo.locations.length > 0 && !matchedBusinessLocation;
    iconSrc = isOffRegisteredBusinessLocation
      ? IconTreasureFeaturedMarker
      : sponsoredMarkerInfo.avatarUrl || IconMarkerPublicWebp;
  }

  return buildMarkerBase({
    capsule,
    position,
    icon: iconSrc,
    businessAvatarUrlOverride:
      normalizedCapsuleType === "sponsored"
        ? String(sponsoredMarkerInfo?.avatarUrl || "").trim()
        : "",
    businessLocationLabel,
    companyName: String(sponsoredMarkerInfo?.companyName || "").trim(),
    senderLabel: resolveMarkerSenderLabel(capsule, usersList),
    useBusinessLabels: isBusinessCapsule,
    width: 70,
    height: 70,
    clusterPolicy:
      capsule?.seeded === true || ALWAYS_VISIBLE_CAPSULE_TYPES.has(normalizedCapsuleType)
        ? "exact_only"
        : "overlap",
  });
}

function resolveDraftPosition(draft) {
  const latCandidates = [
    draft?.location?.lat,
    draft?.location?.latitude,
    draft?.lat,
    draft?.latitude,
  ];
  const lngCandidates = [
    draft?.location?.lng,
    draft?.location?.lon,
    draft?.location?.longitude,
    draft?.lng,
    draft?.lon,
    draft?.longitude,
  ];
  const lat = latCandidates.map((value) => Number(value)).find((value) => Number.isFinite(value));
  const lng = lngCandidates.map((value) => Number(value)).find((value) => Number.isFinite(value));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function buildHomeMapMarkerFromDraft(draft) {
  const position = resolveDraftPosition(draft);
  if (!position) return null;
  return buildMarkerBase({
    capsule: draft,
    position,
    icon: IconDraftCapsule,
    isDraft: true,
    clusterPolicy: "overlap",
  });
}

export function buildHomeMapMarkerFromSentCapsule(capsule, userId = "") {
  const position = resolveDraftPosition(capsule);
  if (!position) return null;
  const isSelfMemory = isSelfOnlyCapsule(capsule, userId);
  return buildMarkerBase({
    capsule,
    position,
    icon: isSelfMemory ? IconTimeCapsuleMemory : IconSentCapsule,
    isSent: true,
    clusterPolicy: "overlap",
  });
}

export function dedupeHomeMapMarkersById(markers = []) {
  const seenIds = new Set();
  return (Array.isArray(markers) ? markers : []).filter((marker) => {
    const markerId = String(marker?.id || "").trim();
    if (!markerId) return true;
    if (seenIds.has(markerId)) return false;
    seenIds.add(markerId);
    return true;
  });
}

export function mergeHomeMapMarkers(...markerCollections) {
  return dedupeHomeMapMarkersById(
    markerCollections.flatMap((collection) =>
      Array.isArray(collection) ? collection.filter(Boolean) : []
    )
  );
}

export function isAlwaysVisibleMarker(marker) {
  return Boolean(marker?.alwaysVisible);
}

export function getHomeMapVisibilityKey(marker, userId = "") {
  if (marker?.isDraft) return "drafts";
  const kind = getCapsuleDisplayKind(marker, userId);
  if (kind === "received") return "received";
  if (kind === "sent") return "buried";
  if (kind === "memory") return "personal";
  return "public";
}

export function filterVisibleHomeMapMarkers(
  markers = [],
  {
    hiddenMarkerIds = new Set(),
    visibility = {},
    userId = "",
  } = {}
) {
  return (Array.isArray(markers) ? markers : []).filter((marker) => {
    const markerId = String(marker?.id || "").trim();
    if (markerId && hiddenMarkerIds?.has?.(markerId)) return false;
    if (isAlwaysVisibleMarker(marker)) return true;
    const key = getHomeMapVisibilityKey(marker, userId);
    return visibility?.[key] !== false;
  });
}
